/**
 * Single source of truth for per-route SEO metadata and JSON-LD.
 *
 * This module is deliberately plain ESM JavaScript (with a colocated
 * siteMeta.d.ts for types) so it can be consumed by BOTH:
 *   - the runtime <SEO> React component (src/components/SEO.tsx), and
 *   - the browserless build-time prerender (scripts/prerender.mjs, run in Node).
 *
 * Keeping one source removes the drift that let llms.txt/humans.txt fall out of
 * sync with the site. Change a title, a description or a social link here — not
 * in two places.
 */

export const SITE_URL = "https://www.geekcow.com";

/**
 * Retired paths that still get traffic, and where they now live. Read by
 * scripts/serve.mjs, the PM2 process Apache proxies to, which turns each into a
 * real 301. The React router has its own copy of this rule in App.tsx, which
 * only matters for navigation inside an already-loaded page.
 */
export const LEGACY_REDIRECTS = {
  "/profile": "/olga",
  // The studio page moved from /agency onto the root, so the root domain's
  // authority lands on the page we want ranked instead of being spent on a
  // one-screen intro that declared /agency canonical and lost the argument
  // with Google ("Duplicate, Google chose different canonical than user").
  "/agency": "/",
};

/** Per-face defaults — title/description/url when a page passes no override. */
export const DEFAULTS = {
  choice: {
    title: "Geekcow — Secure websites & a professional for your team",
    description:
      "Geekcow Creative Studio builds secure, GDPR-ready and accessible websites for European SMEs and NGOs. Olga Molina is also available for freelance and interim work.",
    url: `${SITE_URL}/`,
  },
  agency: {
    title: "Geekcow Creative Studio — Secure, GDPR-ready & accessible websites",
    description:
      "We design and build secure, GDPR/AVG-compliant and WCAG-accessible WordPress websites for European SMEs and NGOs. Audited, not templated.",
    url: `${SITE_URL}/`,
  },
  profile: {
    title: "Olga Molina — Product designer & full-stack developer for hire",
    description:
      "Freelance and interim product design and full-stack development, specialised in security, GDPR/AVG and WCAG accessibility. Based in the Netherlands.",
    url: `${SITE_URL}/olga`,
  },
  project: {
    title: "Olga | Cybersecurity & Full Stack Developer",
    description:
      "Portfolio of Olga Molina. Full Stack Developer and Cybersecurity Specialist focusing on secure, modern web experiences.",
    url: `${SITE_URL}/`,
  },
};

const CREDENTIALS = [
  {
    "@type": "EducationalOccupationalCredential",
    name: "Microsoft Certified: Security, Compliance, and Identity Fundamentals (SC-900)",
    url: "https://learn.microsoft.com/en-us/credentials/certifications/security-compliance-and-identity-fundamentals/",
  },
  {
    "@type": "EducationalOccupationalCredential",
    name: "Google Cybersecurity Professional Certificate",
    url: "https://www.coursera.org/professional-certificates/google-cybersecurity",
  },
  {
    "@type": "EducationalOccupationalCredential",
    name: "ISC2 Candidate",
    url: "https://www.credly.com/badges/3d987ed7-ec90-4c8e-b5c4-1a3fcd212470/public_url",
  },
  {
    "@type": "EducationalOccupationalCredential",
    name: "Building AI Agents with MongoDB",
    url: "https://www.credly.com/badges/570653b1-3573-48a7-9360-61e004e38775/public_url",
  },
];

/** Olga's own profiles — used by the Person schema. */
const SAME_AS = [
  "https://www.linkedin.com/in/olgamolinaleche/",
  "https://github.com/omleche",
  "https://dribbble.com/Molina-Leche",
  "https://www.behance.net/olgamolinaleche",
];

/** The studio's own profiles — used by the Organization schema. */
const SAME_AS_COMPANY = [
  "https://www.linkedin.com/company/geekcow-creative-studio/",
  "https://www.instagram.com/geekcow/",
];

/**
 * Per-project metadata for the /projects/:slug routes, so the prerender can emit
 * a correct title + description + social card for each case study rather than
 * the generic site default. Keep in sync with public/sitemap.xml.
 */
export const PROJECT_META = {
  "Seeds-Afghanistan": {
    title: "Seeds Afghanistan — NGO website redesign",
    description:
      "Website redesign for a humanitarian NGO empowering Afghan communities through sustainable agriculture. Roles: UX/UI Designer, Frontend Developer.",
  },
  cvjungle: {
    title: "CVJungle — SaaS platform design & branding",
    description:
      "Platform design and branding for a Guatemalan SaaS startup simplifying low- and mid-level recruitment in Latin America. Roles: Brand Designer, UX/UI Designer.",
  },
  "la-isla-juice-bar": {
    title: "La Isla Juice Bar — brand identity",
    description:
      "Complete brand identity for a juice bar in Belgium, developed at 360 Graphic Studio. Role: Brand Designer.",
  },
  "crocs-x-christian-cowan": {
    title: "Crocs × Christian Cowan — e-commerce UX",
    description:
      "UX design for an immersive fashion-forward e-commerce experience for a Crocs × Christian Cowan limited-edition campaign (We Are Social Amsterdam). Role: UX Designer.",
  },
  "adwuma-ye-website": {
    title: "Adwuma Ye — inclusive NGO website",
    description:
      "User-centered NGO website enabling donations and product purchases for an African social enterprise. Roles: Web Designer, UX/UI Designer.",
  },
  "sunshine-books": {
    title: "Sunshine Books — React book discovery app",
    description:
      "React.js book discovery web application with Firebase Realtime Database and Tailwind CSS. Roles: UI/UX Design, Web Development.",
  },
  "experience-foundry": {
    title: "Experience Foundry — consultancy website",
    description: "Professional website for a digital transformation consultancy. Role: Web Design.",
  },
  analyticos: {
    title: "Analyticos — analytics dashboard UI",
    description: "Intuitive data analytics dashboard for visualizing complex datasets. Role: UI Design.",
  },
};

function personSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Olga Molina",
    url: `${SITE_URL}/olga`,
    jobTitle: "Product Designer & Full Stack Developer",
    description:
      "Product designer and full-stack developer specialised in security, GDPR/AVG and WCAG accessibility. Available for freelance and interim work across Europe.",
    address: { "@type": "PostalAddress", addressCountry: "NL", addressRegion: "Netherlands" },
    knowsLanguage: ["es", "en", "nl"],
    sameAs: SAME_AS,
    knowsAbout: [
      "React", "TypeScript", "Next.js", "Vue.js", "Node.js", "Tailwind CSS",
      "Figma", "Adobe Creative Suite", "SQL", "MongoDB", "Python", "WordPress",
      "Cybersecurity", "Web Security", "GDPR", "WCAG Accessibility", "CI/CD",
      "Information Architecture", "UX Design", "Brand Identity",
    ],
    hasCredential: CREDENTIALS,
  };
}

function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "GeekCow Creative Studio",
    url: `${SITE_URL}/`,
    description:
      "Secure, GDPR/AVG-compliant and WCAG-accessible WordPress websites for European SMEs and NGOs.",
    founder: { "@type": "Person", name: "Olga Molina" },
    address: { "@type": "PostalAddress", addressLocality: "Weesp", addressCountry: "NL" },
    areaServed: { "@type": "Place", name: "Europe" },
    sameAs: SAME_AS_COMPANY,
    knowsAbout: [
      "WordPress security hardening", "GDPR compliance", "AVG compliance",
      "WCAG 2.2 accessibility", "European Accessibility Act", "Managed WordPress hosting",
    ],
  };
}

function websiteSchema(siteName, description) {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: siteName,
    url: SITE_URL,
    description,
    author: { "@type": "Person", name: "Olga Molina" },
    inLanguage: "en",
  };
}

function breadcrumbSchema(projectTitle, resolvedTitle, resolvedUrl) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Projects", item: `${SITE_URL}/olga#projects` },
      { "@type": "ListItem", position: 3, name: projectTitle || resolvedTitle, item: resolvedUrl },
    ],
  };
}

const AGENCY_FAQ = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What makes a Geekcow website different from an AI-generated one?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Every build goes through an expert security, GDPR/AVG and WCAG accessibility audit before it ships, and the client receives a signed compliance report. The audit layer is the product, not the page itself.",
      },
    },
    {
      "@type": "Question",
      name: "Where is the hosting located?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Client sites run on managed European hosting, which keeps infrastructure and data ownership inside the EU — a straightforward answer to GDPR/AVG questions.",
      },
    },
    {
      "@type": "Question",
      name: "Does the European Accessibility Act apply to my website?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The European Accessibility Act has been in force since June 2025 and covers many digital businesses. Whether it applies to your specific organisation is a question for your legal advisor; Geekcow builds to WCAG so the technical side is already handled.",
      },
    },
    {
      "@type": "Question",
      name: "What services does Geekcow Creative Studio offer?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Custom and template-based WordPress websites, security hardening, GDPR/AVG compliance work, WCAG accessibility, standalone compliance audits, managed maintenance on European hosting, AI-first MVPs, UX/UI design and branding.",
      },
    },
  ],
};

const PROFILE_FAQ = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Is Olga Molina available for freelance or interim work?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Olga takes interim, project and advisory engagements, remote across Europe and on-site in the Randstad, contracted through a Dutch ZZP / eenmanszaak.",
      },
    },
    {
      "@type": "Question",
      name: "What is Olga Molina's specialism?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Product design and full-stack development with a specialisation in security and compliance — secure-by-design architecture, GDPR/AVG and WCAG accessibility, backed by the Microsoft SC-900 and Google Cybersecurity certifications.",
      },
    },
    {
      "@type": "Question",
      name: "Which languages does Olga work in?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Spanish (native), English (fluent) and Dutch at an elementary A2 level.",
      },
    },
  ],
};

/**
 * Resolve everything the head needs for a page, from a set of SEO props. Used by
 * both the runtime component and the prerender so they never disagree.
 *
 * @param {import("./siteMeta").SeoInput} [input]
 * @returns {import("./siteMeta").ResolvedSeo}
 */
export function resolveSeo(input = {}) {
  const { variant = "project", title, description, image = "/og-image.png", url, projectTitle } = input;
  const fallback = DEFAULTS[variant] || DEFAULTS.project;
  const resolvedTitle = title ?? fallback.title;
  const resolvedDescription = description ?? fallback.description;
  const resolvedUrl = url ?? fallback.url;

  const isProjectPage = !!projectTitle || resolvedUrl.includes("/projects/");
  const ogType = isProjectPage ? "article" : "website";

  // The agency face is the studio's own brand; the other faces read as Olga's.
  const siteName = variant === "agency" ? "GeekCow Creative Studio" : "Olga Molina | Geekcow";
  const pageTitle = isProjectPage && projectTitle ? `${resolvedTitle} | Olga Molina` : resolvedTitle;

  const absoluteImage = image.startsWith("http")
    ? image
    : `${SITE_URL}${image.startsWith("/") ? "" : "/"}${image}`;

  const schemas = [
    websiteSchema(siteName, resolvedDescription),
    variant === "agency" ? organizationSchema() : personSchema(),
  ];
  if (isProjectPage) schemas.push(breadcrumbSchema(projectTitle, resolvedTitle, resolvedUrl));
  if (variant === "agency") schemas.push(AGENCY_FAQ);
  if (variant === "profile") schemas.push(PROFILE_FAQ);

  return {
    pageTitle,
    description: resolvedDescription,
    canonical: resolvedUrl,
    ogType,
    siteName,
    absoluteImage,
    schemas,
  };
}

const escapeAttr = (value) =>
  String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// Keep JSON-LD safe to sit inside a <script> element.
const escapeJson = (value) => JSON.stringify(value).replace(/</g, "\\u003c");

/**
 * Render the replaceable <head> block as an HTML string, for the prerender.
 * Mirrors exactly what SEO.tsx emits via <Helmet>.
 *
 * @param {import("./siteMeta").ResolvedSeo} seo
 * @returns {string}
 */
export function renderHeadTags(seo) {
  const lines = [
    `<title>${escapeAttr(seo.pageTitle)}</title>`,
    `<meta name="description" content="${escapeAttr(seo.description)}" />`,
    `<link rel="canonical" href="${escapeAttr(seo.canonical)}" />`,
    `<meta name="format-detection" content="telephone=no, date=no, email=no, address=no" />`,
    `<meta property="og:type" content="${seo.ogType}" />`,
    `<meta property="og:url" content="${escapeAttr(seo.canonical)}" />`,
    `<meta property="og:site_name" content="${escapeAttr(seo.siteName)}" />`,
    `<meta property="og:locale" content="en_US" />`,
    `<meta property="og:title" content="${escapeAttr(seo.pageTitle)}" />`,
    `<meta property="og:description" content="${escapeAttr(seo.description)}" />`,
    `<meta property="og:image" content="${escapeAttr(seo.absoluteImage)}" />`,
    `<meta property="og:image:alt" content="${escapeAttr(seo.pageTitle)}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:url" content="${escapeAttr(seo.canonical)}" />`,
    `<meta name="twitter:title" content="${escapeAttr(seo.pageTitle)}" />`,
    `<meta name="twitter:description" content="${escapeAttr(seo.description)}" />`,
    `<meta name="twitter:image" content="${escapeAttr(seo.absoluteImage)}" />`,
    ...seo.schemas.map((schema) => `<script type="application/ld+json">${escapeJson(schema)}</script>`),
  ];
  return lines.join("\n    ");
}

/**
 * The routes the prerender writes static HTML for. Each maps a URL path to the
 * SEO props that page renders at runtime, so static and hydrated heads agree.
 *
 * @returns {{ path: string; seo: import("./siteMeta").ResolvedSeo }[]}
 */
export function getPrerenderRoutes() {
  const routes = [
    // The studio page IS the front door. /agency is a 301 back to here
    // (LEGACY_REDIRECTS), so it gets no file of its own.
    {
      path: "/",
      input: {
        variant: "agency",
        description:
          "We design and build secure, GDPR/AVG-compliant and WCAG-accessible WordPress websites for European SMEs and NGOs. Audited, not templated — every build ships with a signed compliance report.",
        url: `${SITE_URL}/`,
      },
    },
    {
      path: "/olga",
      input: {
        variant: "profile",
        description:
          "Freelance and interim product design and full-stack development, specialised in security, GDPR/AVG and WCAG accessibility. Based in the Netherlands, available across Europe.",
        url: `${SITE_URL}/olga`,
      },
    },
    { path: "/privacy", input: { variant: "profile", title: "Privacy Policy — Geekcow", url: `${SITE_URL}/privacy` } },
    { path: "/terms", input: { variant: "profile", title: "Terms of Service — Geekcow", url: `${SITE_URL}/terms` } },
    { path: "/cookies", input: { variant: "profile", title: "Cookie Policy — Geekcow", url: `${SITE_URL}/cookies` } },
  ];

  for (const [slug, meta] of Object.entries(PROJECT_META)) {
    routes.push({
      path: `/projects/${slug}`,
      input: {
        variant: "project",
        title: meta.title,
        description: meta.description,
        url: `${SITE_URL}/projects/${slug}`,
        projectTitle: meta.title,
      },
    });
  }

  return routes.map((route) => ({ path: route.path, seo: resolveSeo(route.input) }));
}
